<script setup lang="ts">
// Create 10 slides
import type { SwiperContainer } from "swiper/element"
import type { SwiperOptions } from "swiper/types"

const containerRef = ref<SwiperContainer | null>(null)
const slides = ref(Array.from({ length: 10 }))

const swiper = useSwiper(containerRef)

type Person = Record<string, unknown>

const people_with_duplicates = ref<Person[]>([])
const { get_value: get_localized_value } = useLocalizedValue()

const get_person_value = (person: Person, field: 'image' | 'name' | 'role') => {
    const value = get_localized_value(person[field])
    return value == null ? '' : String(value)
}

function duplicateUntil<T>(arr: T[], targetLength: number): T[] {
    if (arr.length === 0 || targetLength <= 0) return [];
    if (arr.length > 12) return arr

    const result: T[] = [];

    while (result.length < targetLength) {
        const remaining = targetLength - result.length;
        result.push(...arr.slice(0, remaining));
    }

    return result;
}

onMounted(async () => {
    // Access Swiper instance
    // Read more about Swiper instance: https://swiperjs.com/swiper-api#methods--properties
    if (!people) return
    people_with_duplicates.value = duplicateUntil(people, 12)

    await nextTick() // wait for all slides to be in the DOM

    let params: SwiperOptions = {
        centeredSlides: true,
        slidesPerView: 1.7,
        spaceBetween: 2,
        loop: true,
        breakpoints: {
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1444: { slidesPerView: 5 },
        },
        mousewheel:{
            enabled: true,
            forceToAxis: true
        },
    }

    if (autoplay) {
        params = Object.assign(params, {
            autoplay: {
                pauseOnMouseEnter: true,
                delay: 2200
            }
        })
    }

    if (!containerRef.value) return
    Object.assign(containerRef.value, params)
    containerRef.value.initialize() // now Swiper sees all slides
})

const { people, autoplay } = defineProps({
    people: Array as PropType<Person[]>,
    autoplay: Boolean
})

</script>

<template>
    <ClientOnly>
        <swiper-container
            ref="containerRef"
            v-if="people_with_duplicates.length > 0"
            :init="false"
        >
            <swiper-slide v-for="(person, idx) in people_with_duplicates" :key="idx">
                <div class="slide-card">
                    <div class="slide-image">
                        <ResponsiveImage
                            :image-id="get_person_value(person, 'image')"
                            :alt="get_person_value(person, 'name')"
                            sizes="(min-width: 1444px) 17vw, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 15vw"
                            class="w-full h-full object-cover rounded-[5pt]"
                        />
                    </div>
                    <div class="slide-info">
                        <h3>{{ get_person_value(person, 'name') }}</h3>
                        <p>{{ get_person_value(person, 'role') }}</p>
                    </div>
                </div>
            </swiper-slide>
        </swiper-container>
    </ClientOnly>
</template>

<style lang="css" scoped>
swiper-container {
    display: block;
    width: 100%;
    min-width: 0;
}

swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 30rem;
    overflow: visible;
}

swiper-slide {
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(0.95);
    filter: opacity(70%);
    overflow: visible;
}

.swiper-slide-active {
    transform: scale(1);
    filter: opacity(100%);
}

.slide-card {
    width: 100%;
    min-width: 0;
    height: 30rem;
    display: flex;
    flex-direction: column;
    border-radius: 0.5pt;
    overflow: hidden;
}

.slide-image {
    width: 100%;
    flex: 1;          /* takes up all remaining space */
    min-height: 0;    /* important! prevents flex blowout */
    overflow: hidden;
}

.slide-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 5pt;
}

.slide-info {
    padding: 0.75rem 0.25rem;
    flex-shrink: 0;
}

.slide-info h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
}

.slide-info p {
    margin: 0.2rem 0 0;
    font-size: 0.875rem;
    color: #888;
}

</style>
